import { AxiosError } from 'axios';

/**
 * Maps SSO error codes to user-friendly messages
 *
 * @param error - The error code from Azure AD (e.g., 'access_denied', 'user_not_found')
 * @param description - Optional detailed error description from Azure AD
 * @returns User-friendly error message
 */
export function getSsoErrorMessage(error: string | null, description: string | null): string {
  if (!error) {
    return 'An unexpected error occurred during sign-in. Please try again.';
  }

  // Map common SSO/OAuth error codes to user-friendly messages
  switch (error.toLowerCase()) {
    case 'access_denied':
      return 'Access was denied. Please contact your administrator if you believe this is an error.';

    case 'user_not_found':
      return 'No account found for your Microsoft identity. Please contact your administrator to set up your account.';

    case 'account_deactivated':
    case 'user_disabled':
      return 'Your account has been deactivated. Please contact your administrator for assistance.';

    case 'sso_disabled':
    case 'sso_not_configured':
      return 'Single sign-on is not currently available. Please use email and password to sign in.';

    case 'invalid_request':
      return 'The sign-in request was invalid. Please try again.';

    case 'invalid_client':
      return 'There was a configuration error. Please contact your administrator.';

    case 'invalid_grant':
      return 'The sign-in session has expired. Please try again.';

    case 'unauthorized_client':
      return 'This application is not authorized for single sign-on. Please contact your administrator.';

    case 'unsupported_response_type':
    case 'unsupported_grant_type':
      return 'There was a configuration error. Please contact your administrator.';

    case 'server_error':
      return 'The authentication server encountered an error. Please try again later.';

    case 'temporarily_unavailable':
      return 'The authentication service is temporarily unavailable. Please try again in a few minutes.';

    case 'interaction_required':
      return 'Additional verification is required. Please try signing in again.';

    case 'login_required':
      return 'Please sign in to continue.';

    case 'consent_required':
      return 'Your organization requires consent for this application. Please contact your administrator.';

    case 'token_exchange_failed':
      return 'Failed to complete the sign-in process. Please try again.';

    case 'user_not_linked':
      return 'Your Microsoft account is not linked to a preparer account. Please contact your administrator.';

    default:
      // If we have a description, it might be more helpful
      if (description) {
        return description;
      }
      return 'Sign-in failed. Please try again or contact your administrator if the problem persists.';
  }
}

/**
 * Maps authentication errors to human-readable messages
 */
export function getAuthErrorMessage(error: unknown): string {
  // Handle AxiosError
  if (error instanceof Error && 'response' in error) {
    const axiosError = error as AxiosError;
    const status = axiosError.response?.status;

    switch (status) {
      case 401:
        return 'Invalid email or password. Please check your credentials and try again.';
      case 403:
        return 'Access denied. Your account may not have the necessary permissions.';
      case 404:
        return 'Account not found. Please verify your email address.';
      case 429:
        return 'Too many login attempts. Please wait a few minutes before trying again.';
      case 500:
        return 'Server error. Please try again later or contact support if the problem persists.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again in a few moments.';
      default:
        // Check if there's a custom error message from the API
        if (axiosError.response?.data && typeof axiosError.response.data === 'object') {
          const responseData = axiosError.response.data as Record<string, unknown>;
          if (responseData.message && typeof responseData.message === 'string') {
            return responseData.message;
          }
          if (responseData.error && typeof responseData.error === 'string') {
            return responseData.error;
          }
        }
        return 'Login failed. Please check your credentials and try again.';
    }
  }

  // Handle general Error objects
  if (error instanceof Error) {
    // Don't show technical error messages to users
    if (error.message.includes('Network Error')) {
      return 'Network connection error. Please check your internet connection and try again.';
    }
    if (error.message.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }
    // For other generic errors, show a user-friendly message
    return 'Login failed. Please check your credentials and try again.';
  }

  // Fallback for unknown error types
  return 'An unexpected error occurred. Please try again later.';
}

/**
 * Gets a user-friendly error message for any type of error
 * This is a more general function that can be used for other types of errors
 */
export function getErrorMessage(error: unknown, fallback = 'An unexpected error occurred'): string {
  if (error instanceof Error && 'response' in error) {
    const axiosError = error as AxiosError;

    // Try to extract error message from response
    if (axiosError.response?.data && typeof axiosError.response.data === 'object') {
      const responseData = axiosError.response.data as Record<string, unknown>;
      if (responseData.message && typeof responseData.message === 'string') {
        return responseData.message;
      }
      if (responseData.error && typeof responseData.error === 'string') {
        return responseData.error;
      }
    }

    // Fallback to status-based messages
    const status = axiosError.response?.status;
    if (status && status >= 400) {
      return `Request failed (${status}). Please try again.`;
    }
  }

  if (error instanceof Error) {
    // Don't expose technical details to users
    if (error.message.includes('Network Error')) {
      return 'Network connection error. Please check your internet connection and try again.';
    }
    return error.message || fallback;
  }

  return fallback;
}

/**
 * Extracts error message from API response following the nested structure:
 * response.data.error.message
 * This is commonly used with the API client which returns errors in this format.
 */
export function getApiErrorMessage(error: unknown, fallback = 'An error occurred. Please try again.'): string {
  if (error instanceof Error && 'response' in error) {
    const axiosError = error as AxiosError<{
      error?: {
        message?: string;
        code?: string;
      };
      message?: string;
    }>;

    // Try nested error.message first (API client standard format)
    const errorMessage = axiosError.response?.data?.error?.message;
    if (errorMessage && typeof errorMessage === 'string') {
      return errorMessage;
    }

    // Fall back to top-level message
    const topLevelMessage = axiosError.response?.data?.message;
    if (topLevelMessage && typeof topLevelMessage === 'string') {
      return topLevelMessage;
    }
  }

  // Use general error message extraction as fallback
  return getErrorMessage(error, fallback);
}
